import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import SideNavButtons from "../components/Common/SideNavButtons";
import { useCart } from "../context/CartContext.js";
import CategoryHeading from "../components/Common/CategoryHeading.js";
import { items as defaultItems, parentCategories as defaultParentCategories, category_descriptions as defaultCategoryDescriptions } from "../data/items.ts";
import { items as vegasItems, parentCategories as vegasParentCategories, category_descriptions as vegasCategoryDescriptions } from "../data/items-vegas.ts";
import CategoryFilter from "../components/ItemsCatalog/category_filter.js";
import SearchModal from "../components/ItemsCatalog/search_modal.js";
import ItemModal from "../components/ItemsCatalog/item_modal.js";
import SuppressorModal from "../components/ItemsCatalog/SuppressorModal.js";
import ViewControls from "../components/ItemsCatalog/view_controls.js";
import ScreensaverButton from "../components/ItemsCatalog/screensaver_button.js";
import ItemsList from "../components/ItemsCatalog/items_list.js";
import LoadingMessage from "../components/ItemsCatalog/loading_message.js";
import NoItemsMessage from "../components/ItemsCatalog/no_items_message.js";
import "../styles/ItemsCatalog.css";

async function fetchPrices(itemIds) {
  const response = await fetch(
    "https://dat-photo-admin.t9e2i3kal.workers.dev/pricing/batch-get",
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ids: itemIds }),
    }
  );
  const data = await response.json();
  return data.success ? data.data : {};
}

  const ItemsCatalog = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isVegas = searchParams.get("vegas") === "true";

  const items = useMemo(() => isVegas ? vegasItems : defaultItems, [isVegas]);
  const parentCategories = useMemo(() => isVegas ? vegasParentCategories : defaultParentCategories, [isVegas]);
  const category_descriptions = useMemo(() => isVegas ? vegasCategoryDescriptions : defaultCategoryDescriptions, [isVegas]);

  // Helper to update URL params
  const updateUrlParams = useCallback((params) => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Preserve existing special params
    const itemId = urlParams.get("itemid");
    const screensaver = urlParams.get("screensaver");
    const vegas = urlParams.get("vegas");
    
    // Clear filter-related params
    urlParams.delete("parent");
    urlParams.delete("category");
    urlParams.delete("search");
    
    // Set new filter params
    if (params.parent) urlParams.set("parent", params.parent);
    if (params.category) urlParams.set("category", params.category);
    if (params.search) urlParams.set("search", params.search);
    
    // Restore special params
    if (itemId) urlParams.set("itemid", itemId);
    if (screensaver) urlParams.set("screensaver", screensaver);
    if (vegas) urlParams.set("vegas", vegas);
    
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, "", newUrl);
  }, []);

  // State declarations
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedParentCategory, setSelectedParentCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const storedViewMode = sessionStorage.getItem("viewMode");
    const isMobile = window.innerWidth <= 768;
    return storedViewMode && !isMobile ? storedViewMode : isMobile ? "row" : "card";
  });
  const [screensaverMode, setScreensaverMode] = useState(false);
  const [screensaverInterval, setScreensaverInterval] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPick5Filter, setIsPick5Filter] = useState(false);
  const [prices, setPrices] = useState({});
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const [filteredItems, setFilteredItems] = useState([]);
  const [preScreensaverState, setPreScreensaverState] = useState({
    selectedCategories: [],
    selectedParentCategory: null,
    searchTerm: "",
  });
  const [hasProcessedItemId, setHasProcessedItemId] = useState(false);
  const [searchTitle, setSearchTitle] = useState("");
  const [showSuppressorModal, setShowSuppressorModal] = useState(false);
  const [pendingItem, setPendingItem] = useState(null);
  const navigate = useNavigate();

  const handlePrev = () => {
    navigate('/image-gallery');
  };

  const handleNext = () => {
    navigate('/dogtags');
  };

  const cartContext = useCart();
  const { addToCatalogCart, removeFromCatalogCart, getCatalogItemQuantity } = cartContext;

  // Check if item is in cart (returns quantity > 0)
  const isInCart = (itemId) => {
    return getCatalogItemQuantity(itemId) > 0;
  };

  // Function to get item quantity for components
  const getItemQuantity = (itemId) => {
    return getCatalogItemQuantity(itemId);
  };

  // Custom remove function that also removes suppressor if applicable
  const handleRemoveFromCart = (item) => {
    const itemId = typeof item === 'string' ? item : item.id;
    
    // Remove the main item
    removeFromCatalogCart(itemId);
    
    // If this is a suppressed item, also remove its suppressor
    const mainItem = typeof item === 'string' ? items.find(i => i.id === itemId) : item;
    if (mainItem && isSuppressedItem(mainItem)) {
      const suppressorId = `${itemId}_suppressor`;
      removeFromCatalogCart(suppressorId);
    }
  };

  // Function to check if item is in suppressed category
  const isSuppressedItem = (item) => {
    if (!item.category) return false;
    const categories = item.category.split(",").map(cat => cat.trim());
    return categories.includes("Suppressed");
  };

  // Modified add to cart function with suppressor prompt
  const handleAddToCart = (item) => {
    if (isSuppressedItem(item)) {
      setPendingItem(item);
      setShowSuppressorModal(true);
    } else {
      addToCatalogCart(item);
    }
  };

  // Handle suppressor confirmation
  const handleSuppressorConfirm = () => {
    if (pendingItem) {
      // Add the original item
      addToCatalogCart(pendingItem);
      
      // Add the suppressor item
      const suppressorItem = {
        id: `${pendingItem.id}_suppressor`,
        title: `${pendingItem.title} Suppressor`,
        price: "20",
        image: pendingItem.images[0],
        isSuppressor: true
      };
      addToCatalogCart(suppressorItem);
      
      setShowSuppressorModal(false);
      setPendingItem(null);
    }
  };

  // Handle suppressor rejection
  const handleSuppressorCancel = () => {
    if (pendingItem) {
      // Add just the original item without suppressor
      addToCatalogCart(pendingItem);
    }
    setShowSuppressorModal(false);
    setPendingItem(null);
  };

  // Function to disable screensaver
  const disableScreensaver = useCallback(() => {
    if (screensaverMode) {
      if (screensaverInterval) {
        clearInterval(screensaverInterval);
        setScreensaverInterval(null);
      }
      setScreensaverMode(false);
      setSelectedCategories(preScreensaverState.selectedCategories);
      setSelectedParentCategory(preScreensaverState.selectedParentCategory);
      setSearchTerm(preScreensaverState.searchTerm);
    }
  }, [screensaverMode, screensaverInterval, preScreensaverState]);

  // Effect to handle user activity detection for screensaver
  useEffect(() => {
    if (screensaverMode) {
      const activityEvents = ["scroll", "mousemove", "keydown", "click", "touchstart", "touchmove"];
      const handleUserActivity = () => disableScreensaver();

      const timeoutId = setTimeout(() => {
        activityEvents.forEach((event) => {
          document.addEventListener(event, handleUserActivity, { passive: true });
        });
      }, 1000);

      return () => {
        clearTimeout(timeoutId);
        activityEvents.forEach((event) => {
          document.removeEventListener(event, handleUserActivity);
        });
      };
    }
  }, [screensaverMode, disableScreensaver]);

  const allCategories = useMemo(() => {
    const categoryCounts = filteredItems.reduce((acc, item) => {
      item.category
        .split(",")
        .map((cat) => cat.trim())
        .forEach((category) => {
          acc[category] = (acc[category] || 0) + 1;
        });
      return acc;
    }, {});

    return Object.entries(categoryCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([category, count]) => ({ category, count }));
  }, [filteredItems]);

  const getChildCategories = useCallback((parentKey) => {
    return parentCategories[parentKey].map((category) => ({
      category,
      count: allCategories.find((c) => c.category === category)?.count || 0,
    }));
  }, [allCategories, parentCategories]);

  const handleViewModeChange = useCallback((mode) => {
    if (window.innerWidth <= 768) {
      setViewMode("row");
      sessionStorage.setItem("viewMode", "row");
    } else {
      setViewMode(mode);
      sessionStorage.setItem("viewMode", mode);
    }
  }, []);

  const toggleParentCategory = useCallback((parentKey) => {
    disableScreensaver();
    setSearchTerm("");
    setSearchTitle("");
    const isSameParent = selectedParentCategory === parentKey;
    setSelectedParentCategory((prev) => (isSameParent ? null : parentKey));

    if (!isSameParent && parentKey) {
      const children = getChildCategories(parentKey);
      if (children.length > 0) {
        setSelectedCategories([children[0].category]);
        updateUrlParams({
          parent: parentKey,
          category: children[0].category,
          search: ""
        });
      }
    } else {
      setSelectedCategories([]);
      updateUrlParams({
        parent: null,
        category: null,
        search: ""
      });
    }
    setIsPick5Filter(false);
  }, [getChildCategories, selectedParentCategory, disableScreensaver, updateUrlParams]);

  const getParentCategory = useCallback((childCategory) => {
    for (const [parent, children] of Object.entries(parentCategories)) {
      if (children.includes(childCategory)) {
        return parent;
      }
    }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleCategory = useCallback((category, isProgrammaticClick = false) => {
    window.scrollTo({ top: 0 });
    if (!isProgrammaticClick) {
      disableScreensaver();
    }
    setSearchTerm("");
    setSearchTitle("");
    
    const isSelected = selectedCategories.includes(category);
    setSelectedCategories((prev) => (isSelected ? [] : [category]));

    const parent = getParentCategory(category);
    if (parent && parent !== selectedParentCategory) {
      setSelectedParentCategory(parent);
    }

    updateUrlParams({
      parent: isSelected ? null : parent,
      category: isSelected ? null : category,
      search: ""
    });

    setIsPick5Filter(category === "Pick 5");
  }, [getParentCategory, selectedParentCategory, disableScreensaver, updateUrlParams, selectedCategories]);

  const clearAllFilters = useCallback(() => {
    setSelectedCategories([]);
    setSelectedParentCategory(null);
    setSearchTerm("");
    setSearchTitle("");
    updateUrlParams({
      parent: null,
      category: null,
      search: ""
    });
  }, [updateUrlParams]);

  // Fetch prices once on mount with localStorage caching
  useEffect(() => {
    const itemIds = items.map((item) => item.id);
    const cachedPrices = localStorage.getItem('itemsPricesCache');
    
    // Use cached data if available for immediate render
    if (cachedPrices) {
      const parsedPrices = JSON.parse(cachedPrices);
      setPrices(parsedPrices);
      const validItems = items
        .filter((item) => {
          const price = parsedPrices[item.id];
          return price !== undefined && price !== "0" && price !== 0;
        })
        .sort((a, b) => b.likes - a.likes);
      setFilteredItems(validItems);
      setIsLoadingPrices(false);
    }

    // Always make fresh API call in background
    fetchPrices(itemIds)
      .then((fetchedPrices) => {
        // Compare with cached data if exists
        if (cachedPrices) {
          const parsedPrices = JSON.parse(cachedPrices);
          console.log('Comparing prices:', { cached: parsedPrices, fresh: fetchedPrices });
          
          // Deep compare price objects
          const hasChanged = Object.keys(fetchedPrices).some(
            key => fetchedPrices[key] !== parsedPrices[key]
          ) || Object.keys(parsedPrices).some(
            key => parsedPrices[key] !== fetchedPrices[key]
          );

          console.log('Price comparison result:', hasChanged);
          
          if (hasChanged) {
            console.log('Prices changed - updating cache and refreshing');
            localStorage.setItem('itemsPricesCache', JSON.stringify(fetchedPrices));
            window.location.reload(); // Refresh to use new data
            return; // Skip state updates since we're refreshing
          } else {
            console.log('Prices unchanged - no refresh needed');
          }
        } else {
          console.log('No cache exists - storing initial prices');
          localStorage.setItem('itemsPricesCache', JSON.stringify(fetchedPrices));
        }

        // Only update state if we're not refreshing
        setPrices(fetchedPrices);
        const validItems = items
          .filter((item) => {
            const price = fetchedPrices[item.id];
            return price !== undefined && price !== "0" && price !== 0;
          })
          .sort((a, b) => b.likes - a.likes);
        setFilteredItems(validItems);
        setIsLoadingPrices(false);
      })
      .catch((error) => {
        console.error("Error fetching prices:", error);
        setIsLoadingPrices(false);
      });
  }, [items]);

  useEffect(() => {
    // Check query params
    const urlParams = new URLSearchParams(window.location.search);
    const itemId = urlParams.get("itemid");
    
    // Restore filter state from URL if present
    const parent = urlParams.get("parent");
    const category = urlParams.get("category");
    const search = urlParams.get("search");
    
    // Only update state if params have changed
    if (parent && parent !== selectedParentCategory) {
      setSelectedParentCategory(parent);
      if (category && !selectedCategories.includes(category)) {
        setSelectedCategories([category]);
        setIsPick5Filter(category === "Pick 5");
      }
    }
    if (search && search !== searchTerm) {
      setSearchTerm(search);
      setSearchTitle(search);
    }
    
    if (itemId && !hasProcessedItemId) {
      const matchedItem = items.find((item) => item.id === itemId);
      if (matchedItem) {
        setSelectedItem(matchedItem);
        setHasProcessedItemId(true);
      }
    }

    // Check mobile
    const checkIfMobile = () => {
      const isMobileDevice =
        window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        "ontouchstart" in window;
      setIsMobile(isMobileDevice);
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    // Check for screensaver param
    if (urlParams.get("screensaver") === "true") {
      const interval = setInterval(() => {
        const availableCategories = allCategories.filter((cat) =>
          cat.category === "All" ||
          items.some((item) =>
            item.category.split(",").map((c) => c.trim()).includes(cat.category)
          )
        );

        if (availableCategories.length > 0) {
          const randomIndex = Math.floor(Math.random() * availableCategories.length);
          const selectedCategory = availableCategories[randomIndex].category;
          toggleCategory(selectedCategory, true);
        } else {
          toggleCategory("All", true);
        }
      }, 5000);
      setScreensaverInterval(interval);
      setScreensaverMode(true);
    }

    return () => window.removeEventListener("resize", checkIfMobile);
  }, [allCategories, toggleCategory, hasProcessedItemId, selectedParentCategory, selectedCategories, searchTerm, items]);

  const getFilteredItems = useMemo(() => {
    let result = selectedCategories.length === 0
      ? filteredItems
      : filteredItems.filter((item) =>
          item.category.split(",").map((c) => c.trim()).includes(selectedCategories[0])
        );

    if (searchTerm) {
      const normalize = (str) => str.toLowerCase().replace(/[^a-z0-9]/g, "");
      const normalizedTerm = normalize(searchTerm);
      result = result.filter((item) =>
        normalize(item.title).includes(normalizedTerm) ||
        normalize(item.description).includes(normalizedTerm)
      );
    }

    return result;
  }, [selectedCategories, searchTerm, filteredItems]);

  useEffect(() => {
    if (getFilteredItems.length === 0 && !isLoadingPrices) {
      const timer = setTimeout(() => {
        clearAllFilters();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [getFilteredItems.length, isLoadingPrices, clearAllFilters]);

  const handleScreensaverToggle = useCallback(() => {
    if (screensaverMode) {
      disableScreensaver();
    } else {
      setPreScreensaverState({
        selectedCategories: [...selectedCategories],
        selectedParentCategory: selectedParentCategory,
        searchTerm: searchTerm,
      });

      const availableCategories = allCategories.filter((cat) =>
        cat.category === "All" ||
        items.some((item) =>
          item.category.split(",").map((c) => c.trim()).includes(cat.category)
        )
      );

      if (availableCategories.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableCategories.length);
        const selectedCategory = availableCategories[randomIndex].category;
        toggleCategory(selectedCategory, true);
      } else {
        toggleCategory("All", true);
      }

      const interval = setInterval(() => {
        const availableCategories = allCategories.filter((cat) =>
          cat.category === "All" ||
          items.some((item) =>
            item.category.split(",").map((c) => c.trim()).includes(cat.category)
          )
        );

        if (availableCategories.length > 0) {
          const randomIndex = Math.floor(Math.random() * availableCategories.length);
          const selectedCategory = availableCategories[randomIndex].category;
          toggleCategory(selectedCategory, true);
        } else {
          toggleCategory("All", true);
        }
      }, 10000);
      setScreensaverInterval(interval);
      setScreensaverMode(true);
    }
  }, [screensaverMode, selectedCategories, selectedParentCategory, searchTerm, allCategories, toggleCategory, disableScreensaver, items]);

  return (
    <div className="items-catalog">
      <CategoryHeading
        title={
          searchTitle ||
          (selectedCategories.length === 0
            ? "Machine Guns"
            : selectedCategories[0])
        }
        description={
          searchTitle
            ? ""
            : selectedCategories.length > 0 &&
              category_descriptions[selectedCategories[0]]
            ? category_descriptions[selectedCategories[0]]
            : "Drive A Tank also offers additional shooting from our historic collection of machine guns."
        }
      />

      <SideNavButtons
        onPrev={handlePrev}
        onNext={handleNext}
        prevPageName="PHOTOS & VIDEOS"
        nextPageName="DOG TAGS"
        hidden={screensaverMode}
      />

      {!screensaverMode && (
        <CategoryFilter
          selectedCategories={selectedCategories}
          selectedParentCategory={selectedParentCategory}
          allCategories={allCategories}
          parentCategories={parentCategories}
          getChildCategories={getChildCategories}
          toggleParentCategory={toggleParentCategory}
          toggleCategory={toggleCategory}
          isMobile={isMobile}
          onSearchClick={() => {
            clearAllFilters();
            setShowSearchModal(true);
          }}
          onClearFilters={clearAllFilters}
        />
      )}

      {!screensaverMode && (
        <ViewControls
          viewMode={viewMode}
          onViewModeChange={handleViewModeChange}
          onClearFilters={clearAllFilters}
          onSearchClick={() => {
            clearAllFilters();
            setShowSearchModal(true);
          }}
          hasActiveFilters={
            selectedCategories.length > 0 ||
            selectedParentCategory ||
            searchTerm
          }
          isMobile={isMobile}
        />
      )}

      {isLoadingPrices ? (
        <LoadingMessage />
      ) : getFilteredItems.length === 0 ? (
        <NoItemsMessage onClearFilters={clearAllFilters} />
      ) : (
        <ItemsList
          items={getFilteredItems}
          viewMode={viewMode}
          selectedCategories={selectedCategories}
          isPick5Filter={isPick5Filter}
          prices={prices}
          isLoadingPrices={isLoadingPrices}
          isInCart={isInCart}
          onItemClick={setSelectedItem}
          onAddToCart={handleAddToCart}
          onRemoveFromCart={handleRemoveFromCart}
          onCategoryClick={toggleCategory}
          getItemQuantity={getItemQuantity}
        />
      )}

      {selectedItem && (
        <ItemModal
          item={selectedItem}
          allItems={getFilteredItems}
          isPick5Filter={isPick5Filter}
          prices={prices}
          isLoadingPrices={isLoadingPrices}
          isInCart={isInCart}
          onClose={() => setSelectedItem(null)}
          onNext={(nextItem) => setSelectedItem(nextItem)}
          onPrev={(prevItem) => setSelectedItem(prevItem)}
          onAddToCart={handleAddToCart}
          onRemoveFromCart={handleRemoveFromCart}
          onCategoryClick={(category) => {
            toggleCategory(category);
            setSelectedItem(null);
          }}
          getItemQuantity={getItemQuantity}
        />
      )}

      {showSearchModal && (
        <SearchModal
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onClose={() => {
            if (getFilteredItems.length === 0) {
              clearAllFilters();
            } else {
              // Set title to search term and clear description
              setSearchTitle(searchTerm);
              setSelectedParentCategory(null);
              updateUrlParams({
                parent: null,
                category: null,
                search: searchTerm,
              });
            }
            setShowSearchModal(false);
            window.scrollTo({ top: 0 });
          }}
          resultsCount={getFilteredItems.length}
          isMobile={isMobile}
        />
      )}

      <ScreensaverButton
        isActive={screensaverMode}
        onClick={handleScreensaverToggle}
      />

      <SuppressorModal
        isOpen={showSuppressorModal}
        onClose={handleSuppressorCancel}
        onConfirm={handleSuppressorConfirm}
        itemName={pendingItem?.title || ""}
      />
    </div>
  );
};

export default ItemsCatalog;
